import { useEffect, useRef, useState } from "react";
import { useLang } from "@/lib/i18n";

function useInView(ref, options = { threshold: 0.25 }) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setSeen(true);
        obs.disconnect();
      }
    }, options);
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, seen, options]);
  return seen;
}

function CountUp({ from = 0, to, duration = 1800, format = (n) => n }) {
  const [val, setVal] = useState(from);
  const ref = useRef(null);
  const inView = useInView(ref);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, from, to, duration]);
  return <span ref={ref} className="stat-num tabular-nums">{format(val)}</span>;
}

export default function Stats() {
  const { lang, t } = useLang();
  const [tx, setTx] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats`)
      .then((r) => r.json())
      .then((d) => alive && setTx(d.transactions))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (tx == null) return;
    const id = setInterval(() => setTx((v) => (v != null ? v + 1 : v)), 1500);
    return () => clearInterval(id);
  }, [tx]);

  const locale = lang === "fr" ? "fr-CH" : "de-CH";

  const items = [
    {
      ...t("stats.items.years"),
      value: <CountUp to={12} duration={1400} format={(n) => `${n}+`} />,
      icon: "fa-calendar-days",
      color: "#0B1B2B",
    },
    {
      ...t("stats.items.saas"),
      value: <CountUp to={6} duration={1400} format={(n) => `${n}+`} />,
      icon: "fa-rocket",
      color: "#0EA5E9",
    },
    {
      ...t("stats.items.tx"),
      value: <span className="stat-num tabular-nums">{(tx ?? 50000).toLocaleString(locale)}</span>,
      icon: "fa-bolt",
      color: "#D62828",
      live: true,
    },
    {
      ...t("stats.items.vol"),
      value: <CountUp to={2.5} duration={1800} format={(n) => `${n.toFixed(1)}M`} />,
      icon: "fa-chart-line",
      color: "#CA8A04",
    },
  ];

  return (
    <section
      id="stats"
      data-testid="stats-section"
      className="py-20 lg:py-28 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.06] bg-noise pointer-events-none" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(214,40,40,0.18) 0%, rgba(214,40,40,0) 70%)" }} />

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="pill" style={{ background: "transparent", color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.18)" }}>
              <i className="fa-solid fa-chart-simple" /> {t("stats.pill")}
            </span>
            <h2 className="mt-5 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-[20ch]">
              {t("stats.titleLead")}
              <span className="italic font-light text-white/70">{t("stats.titleHighlight")}</span>
              {t("stats.titleTail")}
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-[15px] leading-relaxed">{t("stats.paragraph")}</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
          {items.map((it, i) => (
            <div
              key={i}
              data-testid={`stat-card-${i}`}
              className="bg-[hsl(var(--ink))] p-7 lg:p-9 relative group"
            >
              <div className="flex items-center justify-between mb-7">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: `${it.color}22`, color: it.color }}
                >
                  <i className={`fa-solid ${it.icon}`} />
                </div>
                {it.live && (
                  <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-[hsl(var(--accent))] flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))] pulse-dot" />
                    {t("stats.live")}
                  </span>
                )}
              </div>
              <div className="text-[44px] sm:text-[56px] leading-none">{it.value}</div>
              <div className="mt-4 text-sm font-medium text-white/85">{it.label}</div>
              <div className="mt-1 text-xs text-white/45 font-mono">{it.hint}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
